import { Component, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { connectState } from '@examdojo/angular/util';
import { ButtonComponent } from '@examdojo/core/button';
import { NumberInputComponent } from '@examdojo/core/input';
import { SuperLatexComponent, SuperLatexConnection, SuperLatexContent } from '@examdojo/super-latex';
import { MarkdownViewerComponent } from '@examdojo/ui/markdown-viewer';
import { IonContent, IonFooter, IonHeader, IonModal, IonTitle, IonToolbar } from '@ionic/angular/standalone';
import { combineLatest, map, startWith, tap } from 'rxjs';

@Component({
  selector: 'exam-basic-quadratic-calculator',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MarkdownViewerComponent,
    SuperLatexComponent,
    IonModal,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonFooter,
    ButtonComponent,
    NumberInputComponent,
  ],
  templateUrl: './basic-quadratic-calculator.component.html',
  styleUrls: ['./basic-quadratic-calculator.component.scss'],
})
export class BasicQuadraticCalculatorComponent {
  constructor() {
    this.bAbsCtrl.valueChanges
      .pipe(
        tap((abs) => {
          const sign = this.bCtrl.value >= 0 ? 1 : -1;
          this.bCtrl.setValue(sign * abs, { emitEvent: false });
        }),
        takeUntilDestroyed(),
      )
      .subscribe();

    this.cAbsCtrl.valueChanges
      .pipe(
        tap((abs) => {
          const sign = this.cCtrl.value >= 0 ? 1 : -1;
          this.cCtrl.setValue(sign * abs, { emitEvent: false });
        }),
        takeUntilDestroyed(),
      )
      .subscribe();

    this.bCtrl.valueChanges
      .pipe(
        tap((val) => {
          this.bAbsCtrl.setValue(Math.abs(val), { emitEvent: false });
        }),
        takeUntilDestroyed(),
      )
      .subscribe();

    this.cCtrl.valueChanges
      .pipe(
        tap((val) => {
          this.cAbsCtrl.setValue(Math.abs(val), { emitEvent: false });
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  readonly currentSlide = signal(1);
  readonly isModalOpen = signal(false);

  readonly aCtrl = new FormControl<number>(3, {
    nonNullable: true,
    validators: [Validators.required, this.nonZeroValidator],
  });
  readonly bCtrl = new FormControl<number>(-8, { nonNullable: true, validators: [Validators.required] });
  readonly cCtrl = new FormControl<number>(4, { nonNullable: true, validators: [Validators.required] });

  readonly bAbsCtrl = new FormControl<number>(8, { nonNullable: true, validators: [Validators.required] });
  readonly cAbsCtrl = new FormControl<number>(4, { nonNullable: true, validators: [Validators.required] });

  readonly roundingCtrl = new FormControl<number>(2, {
    nonNullable: true,
    validators: [Validators.required, Validators.min(0), Validators.max(10)],
  });

  private readonly allValues$ = combineLatest([
    this.aCtrl.valueChanges.pipe(startWith(this.aCtrl.value)),
    this.bCtrl.valueChanges.pipe(startWith(this.bCtrl.value)),
    this.cCtrl.valueChanges.pipe(startWith(this.cCtrl.value)),
  ]).pipe(
    map(() => ({
      a: this.aCtrl.value,
      b: this.bCtrl.value,
      c: this.cCtrl.value,
    })),
  );

  private readonly allValuesWithRounding$ = combineLatest([
    this.allValues$,
    this.roundingCtrl.valueChanges.pipe(startWith(this.roundingCtrl.value)),
  ]).pipe(map(([values, rounding]) => ({ ...values, rounding })));

  readonly state = connectState({
    a: this.aCtrl.valueChanges.pipe(startWith(this.aCtrl.value)),
    b: this.bCtrl.valueChanges.pipe(startWith(this.bCtrl.value)),
    c: this.cCtrl.valueChanges.pipe(startWith(this.cCtrl.value)),
    rounding: this.roundingCtrl.valueChanges.pipe(startWith(this.roundingCtrl.value)),
    isValid: this.aCtrl.statusChanges.pipe(
      startWith(this.aCtrl.status),
      map(() => this.aCtrl.valid),
    ),
    discriminant: this.allValuesWithRounding$.pipe(
      map(({ a, b, c, rounding }) => this.round(b * b - 4 * a * c, rounding)),
    ),
    hasRealRoots: this.allValues$.pipe(map(({ a, b, c }) => b * b - 4 * a * c > 0)),
    hasSingleRoot: this.allValues$.pipe(map(({ a, b, c }) => b * b - 4 * a * c === 0)),
    hasNoRealRoots: this.allValues$.pipe(map(({ a, b, c }) => b * b - 4 * a * c < 0)),
    sqrtDiscriminant: this.allValuesWithRounding$.pipe(
      map(({ a, b, c, rounding }) => this.round(Math.sqrt(Math.abs(b * b - 4 * a * c)), rounding)),
    ),
    x1: this.allValuesWithRounding$.pipe(
      map(({ a, b, c, rounding }) => {
        const discriminant = b * b - 4 * a * c;
        if (discriminant < 0) {
          return null;
        }
        return this.round((-b + Math.sqrt(discriminant)) / (2 * a), rounding);
      }),
    ),
    x2: this.allValuesWithRounding$.pipe(
      map(({ a, b, c, rounding }) => {
        const discriminant = b * b - 4 * a * c;
        if (discriminant < 0) {
          return null;
        }
        return this.round((-b - Math.sqrt(discriminant)) / (2 * a), rounding);
      }),
    ),
    discriminantLatex: this.allValuesWithRounding$.pipe(
      map(({ a, b, c, rounding }) => {
        const discriminant = this.round(b * b - 4 * a * c, rounding);
        const comparison = discriminant > 0 ? ' > 0' : discriminant === 0 ? ' = 0' : ' < 0';
        return `$$= (${b})^2 - 4 \\cdot ${a} \\cdot ${c} = ${discriminant}${comparison}$$`;
      }),
    ),
    substitutionLatex: this.allValuesWithRounding$.pipe(
      map(({ a, b, c, rounding }) => {
        const discriminant = this.round(b * b - 4 * a * c, rounding);
        const sqrtDiscriminant = this.round(Math.sqrt(Math.abs(discriminant)), rounding);
        const x1 = discriminant >= 0 ? this.round((-b + Math.sqrt(discriminant)) / (2 * a), rounding) : null;
        const x2 = discriminant >= 0 ? this.round((-b - Math.sqrt(discriminant)) / (2 * a), rounding) : null;

        return `$$= \\frac{-(${b}) \\pm \\sqrt{${discriminant}}}{2 \\cdot ${a}} = \\frac{${-b} \\pm ${sqrtDiscriminant}}{${
          2 * a
        }} = \\begin{cases} \\frac{${-b}+${sqrtDiscriminant}}{${2 * a}} = ${
          x1 !== null ? x1 : ''
        } \\\\ \\frac{${-b}-${sqrtDiscriminant}}{${2 * a}} = ${x2 !== null ? x2 : ''} \\end{cases}$$`;
      }),
    ),
    step1EquationLatex: this.allValues$.pipe(
      map(({ a, b, c }) => {
        return `$$${a}x^2 ${b >= 0 ? '+' : '-'} ${Math.abs(b)}x ${c >= 0 ? '+' : '-'} ${Math.abs(c)} = 0$$`;
      }),
    ),
    step1AnnotationLatex: this.allValues$.pipe(
      map(() => {
        return `$$= a \\quad = b \\quad = c$$`;
      }),
    ),
    step1SubstitutionLatex: this.allValues$.pipe(
      map(({ a, b, c }) => {
        const discriminant = b * b - 4 * a * c;
        return `$$= (${b})^2 - 4 \\cdot ${a} \\cdot ${c} = ${discriminant}$$`;
      }),
    ),
    step2EquationLatex: this.allValues$.pipe(
      map(({ a, b, c }) => {
        return `$$\\htmlData{anchor=coef-a}{${a}}x^2 ${b >= 0 ? '+' : '-'} \\htmlData{anchor=coef-b}{${Math.abs(b)}}x ${
          c >= 0 ? '+' : '-'
        } \\htmlData{anchor=coef-c}{${Math.abs(c)}} = 0$$`;
      }),
    ),
    step2SubstitutionLatex: this.allValues$.pipe(
      map(({ a, b, c }) => {
        const discriminant = b * b - 4 * a * c;
        return `$$= (\\htmlData{anchor=subst-b}{${b}})^2 - 4 \\cdot \\htmlData{anchor=subst-a}{${a}} \\cdot \\htmlData{anchor=subst-c}{${c}} = ${discriminant}$$`;
      }),
    ),
    step2Connections: this.allValues$.pipe(
      map(() => {
        const connections: SuperLatexConnection[] = [
          {
            id: 'conn-a',
            from_anchor: 'coef-a',
            to_anchor: 'label-a',
            style: {
              color: 'sky',
              width: 2,
              from_socket: 'Bottom',
              to_socket: 'Bottom',
            },
          },
          {
            id: 'conn-b',
            from_anchor: 'coef-b',
            to_anchor: 'label-b',
            style: {
              color: 'red',
              width: 2,
              from_socket: 'Top',
              to_socket: 'Top',
            },
          },
          {
            id: 'conn-c',
            from_anchor: 'coef-c',
            to_anchor: 'label-c',
            style: {
              color: 'orange',
              width: 2,
              from_socket: 'Bottom',
              to_socket: 'Bottom',
            },
          },
        ];
        return connections;
      }),
    ),
    step2SuperLatexContent: combineLatest([
      this.allValues$.pipe(
        map(({ a, b, c }) => {
          return `$$\\htmlData{anchor=coef-a}{${a}}x^2 ${b >= 0 ? '+' : '-'} \\htmlData{anchor=coef-b}{${Math.abs(
            b,
          )}}x ${c >= 0 ? '+' : '-'} \\htmlData{anchor=coef-c}{${Math.abs(c)}} = 0$$`;
        }),
      ),
      this.allValues$.pipe(
        map(() => {
          const connections: SuperLatexConnection[] = [
            {
              id: 'conn-a',
              from_anchor: 'coef-a',
              to_anchor: 'label-a',
              style: {
                color: 'sky',
                width: 2,
                from_socket: 'Bottom',
                to_socket: 'Bottom',
              },
            },
            {
              id: 'conn-b',
              from_anchor: 'coef-b',
              to_anchor: 'label-b',
              style: {
                color: 'red',
                width: 2,
                from_socket: 'Top',
                to_socket: 'Top',
              },
            },
            {
              id: 'conn-c',
              from_anchor: 'coef-c',
              to_anchor: 'label-c',
              style: {
                color: 'orange',
                width: 2,
                from_socket: 'Bottom',
                to_socket: 'Bottom',
              },
            },
          ];
          return connections;
        }),
      ),
    ]).pipe(
      map(([latex, connections]) => {
        const content: SuperLatexContent = {
          latex_nodes: [{ latex }],
          connections,
        };
        return content;
      }),
    ),
  });

  formatFraction(numerator: number, denominator: number): string {
    if (denominator === 1) {
      return `${numerator}`;
    }
    if (numerator % denominator === 0) {
      return `${numerator / denominator}`;
    }
    return `\\frac{${numerator}}{${denominator}}`;
  }

  round(value: number, decimals: number): number {
    return Number(value.toFixed(decimals));
  }

  getEquationLatex(): string {
    const a = this.state.a;
    const b = this.state.b;
    const c = this.state.c;
    const aStr = a === 1 ? '' : a === -1 ? '-' : `${a}`;
    const bStr = b === 0 ? '' : b > 0 ? `+ ${b}x` : b === -1 ? '- x' : `${b}x`.replace('-', '- ');
    const cStr = c === 0 ? '' : c > 0 ? `+ ${c}` : `${c}`.replace('-', '- ');

    return `${aStr}x^2 ${bStr} ${cStr} = 0`;
  }

  nextSlide() {
    if (this.currentSlide() < 3) {
      this.currentSlide.set(this.currentSlide() + 1);
    }
  }

  prevSlide() {
    if (this.currentSlide() > 1) {
      this.currentSlide.set(this.currentSlide() - 1);
    }
  }

  openModal() {
    this.isModalOpen.set(true);
  }

  closeModal() {
    this.isModalOpen.set(false);
  }

  private nonZeroValidator(control: AbstractControl): ValidationErrors | null {
    return control.value === 0 ? { nonZero: true } : null;
  }
}
